import { useState } from "react";
import { ChevronsUpDown } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Command, CommandEmpty, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export type MultiPickOption = { value: string; label: string };

export function MultiPick({ label, options, value, onChange }: {
  label: string;
  options: MultiPickOption[];
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = new Set(value);
  const caption = value.length === 0
    ? "All"
    : value.length === 1
      ? options.find((option) => option.value === value[0])?.label ?? value[0]
      : `${value.length} selected`;
  const toggle = (option: string) => onChange(selected.has(option)
    ? value.filter((item) => item !== option)
    : [...value, option]);

  return <div className="grid w-56 max-w-full gap-1.5">
    <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{label}</span>
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button data-nonmutating type="button" variant="outline" role="combobox" aria-label={label} aria-expanded={open} className="justify-between font-normal">
          <span className="truncate">{caption}</span><ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
        <Command><CommandList><CommandEmpty>No matching option.</CommandEmpty>
          {options.map((option) => <CommandItem key={option.value} value={`${option.label} ${option.value}`} onSelect={() => toggle(option.value)}>
            <Checkbox data-nonmutating checked={selected.has(option.value)} tabIndex={-1} aria-label={option.label} className="pointer-events-none" />
            <span>{option.label}</span>
          </CommandItem>)}
        </CommandList></Command>
      </PopoverContent>
    </Popover>
  </div>;
}
